import React, { useEffect, useState } from "react";
import { MessageSquare, Send, RefreshCw, CheckCheck, Bot, Copy, Sparkles, CheckCircle2, Shield, Globe2, Plus, X, AlertTriangle, Settings, PlayCircle, ChevronDown, ChevronUp, Save } from "lucide-react";
import { api, formatApiError } from "../api/client";

const ACTION_META = {
  create_ride: { label: "Carrera creada", color: "success" },
  cancel_ride: { label: "Cancelación", color: "danger" },
  query_status: { label: "Consulta", color: "info" },
  reply_only: { label: "Info", color: "info" },
  handoff: { label: "Operador", color: "warning" },
  driver_assigned: { label: "Asignado", color: "success" },
};

export default function BotView() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState(null);
  const [newPhone, setNewPhone] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTester, setShowTester] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [instructions, setInstructions] = useState("");
  const [tone, setTone] = useState("amable_peruano");
  const [useEmojis, setUseEmojis] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [dirty, setDirty] = useState(false);
  const [testerInput, setTesterInput] = useState("");
  const [testerHistory, setTesterHistory] = useState([]);
  const [testerBusy, setTesterBusy] = useState(false);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/whatsapp/2chat/webhook`;

  const loadStatus = () => api.get("/whatsapp/2chat/status").then((r) => setStatus(r.data)).catch(() => {});
  const loadConfig = () =>
    api.get("/whatsapp/2chat/config").then((r) => {
      setConfig(r.data);
      setGreeting(r.data.greeting_message || "");
      setInstructions(r.data.custom_instructions || "");
      setTone(r.data.tone || "amable_peruano");
      setUseEmojis(r.data.use_emojis !== false);
      setCompanyName(r.data.company_name || "Turismo MDD");
      setCompanyCity(r.data.company_city || "Puerto Maldonado, Perú");
      setDirty(false);
    }).catch(() => {});
  const loadConvs = () =>
    api.get("/whatsapp/2chat/conversations").then((r) => setConversations(r.data)).catch((e) => setErr(formatApiError(e)));
  const loadMsgs = (phone) =>
    api
      .get(`/whatsapp/2chat/messages/${encodeURIComponent(phone)}`)
      .then((r) => setMessages(r.data))
      .catch(() => setMessages([]));

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadConvs();
    const t = setInterval(loadConvs, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadMsgs(selected.from_number);
    const t = setInterval(() => loadMsgs(selected.from_number), 5000);
    return () => clearInterval(t);
  }, [selected]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/whatsapp/2chat/send", { to: selected.from_number, text: reply });
      setReply("");
      loadMsgs(selected.from_number);
    } catch (e2) {
      alert(formatApiError(e2));
    } finally {
      setSending(false);
    }
  };

  const copyWebhook = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(webhookUrl).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          },
          () => fallbackCopy()
        );
      } else {
        fallbackCopy();
      }
    } catch {
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = webhookUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silencioso */
    }
  };

  const subscribeWebhook = async () => {
    try {
      const publicUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await api.post("/whatsapp/2chat/subscribe", {
        public_url: publicUrl,
        event: "whatsapp.message.received",
      });
      alert("Suscripción enviada a 2chat:\n\n" + JSON.stringify(res.data.twochat_response, null, 2));
    } catch (e2) {
      alert(formatApiError(e2));
    }
  };

  // ----- Config helpers -----
  const saveBotSettings = async () => {
    setSavingCfg(true);
    try {
      const res = await api.patch("/whatsapp/2chat/config", {
        greeting_message: greeting,
        custom_instructions: instructions,
        tone,
        use_emojis: useEmojis,
        company_name: companyName,
        company_city: companyCity,
      });
      setConfig(res.data);
      setDirty(false);
    } catch (e2) {
      alert(formatApiError(e2));
    } finally {
      setSavingCfg(false);
    }
  };

  const sendTester = async () => {
    if (!testerInput.trim() || testerBusy) return;
    const userMsg = testerInput.trim();
    setTesterInput("");
    setTesterHistory((h) => [...h, { role: "user", text: userMsg }]);
    setTesterBusy(true);
    try {
      const res = await api.post("/whatsapp/2chat/test-bot", { message: userMsg });
      const d = res.data.decision || {};
      setTesterHistory((h) => [...h, {
        role: "bot",
        text: d.reply || "(sin respuesta)",
        action: d.action,
      }]);
    } catch (e2) {
      setTesterHistory((h) => [...h, { role: "bot", text: formatApiError(e2), action: "error" }]);
    } finally {
      setTesterBusy(false);
    }
  };

  const patchConfig = async (payload) => {
    setSavingCfg(true);
    try {
      const res = await api.patch("/whatsapp/2chat/config", payload);
      setConfig(res.data);
    } catch (e2) {
      alert(formatApiError(e2));
    } finally {
      setSavingCfg(false);
    }
  };

  const addWhitelistPhone = async () => {
    let p = newPhone.trim().replace(/[\s-]/g, "");
    if (!p) return;
    if (!p.startsWith("+")) p = "+" + p.replace(/^0+/, "");
    if ((config?.whitelist || []).includes(p)) {
      setNewPhone("");
      return;
    }
    await patchConfig({ whitelist: [...(config?.whitelist || []), p] });
    setNewPhone("");
  };

  const removeWhitelistPhone = async (p) => {
    await patchConfig({ whitelist: (config?.whitelist || []).filter((x) => x !== p) });
  };

  const setMode = async (mode) => {
    if (mode === "public") {
      const ok = window.confirm(
        "⚠️ Activación general\n\nEl bot va a responder AUTOMÁTICAMENTE a cualquier número que le escriba.\n\n¿Confirmas la activación pública?"
      );
      if (!ok) return;
      try {
        setSavingCfg(true);
        const res = await api.post("/whatsapp/2chat/config/activate-public");
        setConfig(res.data);
      } catch (e2) {
        alert(formatApiError(e2));
      } finally {
        setSavingCfg(false);
      }
    } else {
      await patchConfig({ mode: "whitelist" });
    }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="bot-view">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#3949AB]">Comunicaciones</div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            Bot WhatsApp <Sparkles className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Automatizado con <b className="text-[#3949AB]">Claude 4.6 IA</b> · Canal <b>2chat.io</b>
          </p>
        </div>
        {status && (
          <div className="flex items-center gap-2 flex-wrap">
            {status.configured ? (
              <span className="chip chip-success">
                <Bot className="w-3 h-3" strokeWidth={2.5} />
                Configurado
              </span>
            ) : (
              <span className="chip chip-danger">Sin configurar</span>
            )}
            {status.from_number && (
              <span className="text-xs text-neutral-500 font-mono">{status.from_number}</span>
            )}
            <span className="chip chip-info !text-[10px]">{status.ai_model}</span>
          </div>
        )}
      </header>

      {/* Setup panel */}
      <div className="office-card p-4 bg-gradient-to-r from-[#EEF0FC] to-white border-l-4 border-[#3949AB]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="font-semibold text-sm mb-1 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#3949AB]" strokeWidth={2.5} /> Configuración 2chat
            </div>
            <div className="text-xs text-neutral-600 space-y-1">
              <div>
                1. Entra a <a href="https://app.2chat.io" target="_blank" rel="noreferrer" className="text-[#3949AB] underline">app.2chat.io</a> → <b>Webhooks</b> → <b>Create Webhook</b>
              </div>
              <div>
                2. Evento: <span className="font-mono bg-white px-1.5 py-0.5 rounded border">whatsapp.message.received</span>
              </div>
              <div>3. Pega esta URL como Hook URL:</div>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-[11px] bg-white p-2 rounded border border-neutral-300 break-all">
                  {webhookUrl}
                </code>
                <button
                  onClick={copyWebhook}
                  className="btn-ghost !p-2"
                  title="Copiar"
                  data-testid="copy-webhook-btn"
                >
                  {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="pt-1">
                O usa el botón para <b>auto-suscribir</b> el webhook (requiere admin):
                <button
                  onClick={subscribeWebhook}
                  className="btn-primary ml-2 !text-[11px] !py-1 !px-2"
                  data-testid="auto-subscribe-btn"
                >
                  Auto-suscribir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODO PRUEBAS / WHITELIST */}
      {config && (
        <div
          className={`office-card p-4 border-l-4 ${
            config.mode === "public" ? "border-emerald-500 bg-emerald-50/40" : "border-amber-500 bg-amber-50/40"
          }`}
          data-testid="bot-mode-card"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-1">
                {config.mode === "public" ? (
                  <>
                    <Globe2 className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                    <span className="font-semibold text-sm">Modo público — Bot activo para TODOS</span>
                    <span className="chip chip-success !text-[10px]">ACTIVACIÓN GENERAL</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 text-amber-600" strokeWidth={2.5} />
                    <span className="font-semibold text-sm">Modo pruebas — Solo números autorizados</span>
                    <span className="chip chip-warning !text-[10px]">WHITELIST</span>
                  </>
                )}
              </div>
              <div className="text-xs text-neutral-600">
                {config.mode === "public"
                  ? "El bot responde automáticamente a cualquier pasajero que escriba al WhatsApp de la empresa."
                  : "Durante pruebas, el bot solo responde a los teléfonos de la lista. Los demás mensajes se registran pero no reciben respuesta automática."}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {config.mode === "whitelist" ? (
                <button
                  onClick={() => setMode("public")}
                  disabled={savingCfg}
                  className="btn-primary !bg-emerald-600 hover:!bg-emerald-700"
                  data-testid="activate-public-btn"
                >
                  <Globe2 className="w-4 h-4" strokeWidth={2.5} />
                  Activación general
                </button>
              ) : (
                <button
                  onClick={() => setMode("whitelist")}
                  disabled={savingCfg}
                  className="btn-ghost !border !border-amber-400 !text-amber-700"
                  data-testid="deactivate-public-btn"
                >
                  <Shield className="w-4 h-4" strokeWidth={2.5} />
                  Volver a modo pruebas
                </button>
              )}
            </div>
          </div>

          {config.mode === "whitelist" && (
            <div className="mt-4 pt-3 border-t border-amber-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-600" /> Números autorizados
                <span className="text-neutral-400 font-normal normal-case tracking-normal">
                  ({(config.whitelist || []).length})
                </span>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <input
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addWhitelistPhone()}
                  placeholder="+51987654321"
                  className="office-input flex-1 max-w-xs !text-sm"
                  data-testid="whitelist-phone-input"
                />
                <button
                  onClick={addWhitelistPhone}
                  disabled={savingCfg || !newPhone.trim()}
                  className="btn-primary !py-1.5"
                  data-testid="whitelist-add-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.5} />
                  Agregar
                </button>
              </div>

              {(config.whitelist || []).length === 0 ? (
                <div className="text-xs text-neutral-500 italic">
                  Sin números autorizados aún. Agrega al menos uno para poder probar el bot.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(config.whitelist || []).map((p) => (
                    <span
                      key={p}
                      className="chip chip-info !py-1 !px-2 gap-2 group"
                      data-testid={`whitelist-item-${p}`}
                    >
                      <span className="font-mono">{p}</span>
                      <button
                        onClick={() => removeWhitelistPhone(p)}
                        disabled={savingCfg}
                        className="opacity-60 hover:opacity-100"
                        title="Quitar"
                        data-testid={`whitelist-remove-${p}`}
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {err && <div className="chip chip-danger">{err}</div>}

      {/* ============= PERSONALIZACIÓN DEL BOT ============= */}
      {config && (
        <div className="office-card overflow-hidden" data-testid="bot-settings-card">
          <button
            onClick={() => setShowSettings((v) => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition"
            data-testid="toggle-settings-btn"
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#3949AB]" strokeWidth={2.5} />
              <span className="font-semibold text-sm">Personalizar mensajes y comportamiento del bot</span>
              {dirty && <span className="chip chip-warning !text-[10px]">Cambios sin guardar</span>}
            </div>
            {showSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showSettings && (
            <div className="p-4 border-t border-neutral-200 space-y-4">
              {/* Empresa */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">
                    Nombre de la empresa
                  </label>
                  <input
                    className="office-input w-full !text-sm"
                    value={companyName}
                    onChange={(e) => { setCompanyName(e.target.value); setDirty(true); }}
                    placeholder="Turismo MDD"
                    data-testid="bot-company-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">
                    Ciudad
                  </label>
                  <input
                    className="office-input w-full !text-sm"
                    value={companyCity}
                    onChange={(e) => { setCompanyCity(e.target.value); setDirty(true); }}
                    placeholder="Puerto Maldonado, Perú"
                    data-testid="bot-company-city"
                  />
                </div>
              </div>

              {/* Saludo */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">
                  Mensaje de bienvenida (saludo inicial)
                </label>
                <textarea
                  className="office-input w-full !text-sm min-h-[70px] font-mono"
                  value={greeting}
                  onChange={(e) => { setGreeting(e.target.value); setDirty(true); }}
                  placeholder="¡Hola! 🚕 Bienvenido a Turismo MDD..."
                  data-testid="bot-greeting"
                />
                <div className="text-[11px] text-neutral-500 mt-1">
                  Este texto se envía cuando un pasajero saluda por primera vez. Usa <code>*negrita*</code> con asteriscos (formato WhatsApp) y todos los emojis que quieras.
                </div>
              </div>

              {/* Instrucciones personalizadas */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">
                  Instrucciones extra para el bot (opcional)
                </label>
                <textarea
                  className="office-input w-full !text-sm min-h-[90px] font-mono"
                  value={instructions}
                  onChange={(e) => { setInstructions(e.target.value); setDirty(true); }}
                  placeholder="Ejemplo: Siempre pregunta cuántas personas viajan. Si el pasajero tiene equipaje voluminoso, ofrécele el servicio de camioneta. Los sábados hay tarifa nocturna después de las 10pm..."
                  data-testid="bot-instructions"
                />
                <div className="text-[11px] text-neutral-500 mt-1">
                  Escribe reglas del negocio, preguntas obligatorias, políticas de descuento, promociones. El bot las respetará al 100%.
                </div>
              </div>

              {/* Tono + Emojis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">
                    Tono de voz
                  </label>
                  <select
                    className="office-input w-full !text-sm"
                    value={tone}
                    onChange={(e) => { setTone(e.target.value); setDirty(true); }}
                    data-testid="bot-tone"
                  >
                    <option value="amable_peruano">🇵🇪 Amable peruano (cálido, con confianza)</option>
                    <option value="formal">👔 Formal (usted, profesional)</option>
                    <option value="casual_joven">😎 Casual joven (informal, tú)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">
                    Uso de emojis
                  </label>
                  <label className="flex items-center gap-2 p-2 rounded border border-neutral-300 cursor-pointer bg-white hover:bg-neutral-50" data-testid="bot-emojis-toggle">
                    <input
                      type="checkbox"
                      checked={useEmojis}
                      onChange={(e) => { setUseEmojis(e.target.checked); setDirty(true); }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">
                      {useEmojis ? "🎉 Usar emojis en respuestas (recomendado)" : "Texto plano sin emojis"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  onClick={() => { loadConfig(); }}
                  className="btn-ghost !text-xs"
                  disabled={!dirty || savingCfg}
                  data-testid="bot-cancel-btn"
                >
                  Descartar
                </button>
                <button
                  onClick={saveBotSettings}
                  className="btn-primary !text-xs"
                  disabled={!dirty || savingCfg}
                  data-testid="bot-save-btn"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={2.5} />
                  {savingCfg ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============= PROBAR EL BOT ============= */}
      <div className="office-card overflow-hidden" data-testid="bot-tester-card">
        <button
          onClick={() => setShowTester((v) => !v)}
          className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition"
          data-testid="toggle-tester-btn"
        >
          <div className="flex items-center gap-2">
            <PlayCircle className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
            <span className="font-semibold text-sm">Probar el bot (chat de simulación)</span>
            <span className="chip chip-info !text-[10px]">Sin enviar WhatsApp real</span>
          </div>
          {showTester ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showTester && (
          <div className="p-4 border-t border-neutral-200">
            <div className="bg-[#F8F9FC] rounded-lg p-3 min-h-[240px] max-h-[400px] overflow-y-auto space-y-2 mb-3">
              {testerHistory.length === 0 ? (
                <div className="text-center text-neutral-400 text-xs py-10">
                  Escribe un mensaje abajo (ej: &quot;Hola&quot;) para ver cómo respondería el bot con la configuración actual.
                </div>
              ) : (
                testerHistory.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-line ${
                      m.role === "user" ? "bg-[#3949AB] text-white" : "bg-white border border-neutral-200"
                    }`}>
                      {m.text}
                      {m.action && (
                        <div className="text-[10px] mt-1 opacity-60">
                          <span className="chip !text-[9px] !py-0 !px-1">action: {m.action}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {testerBusy && (
                <div className="flex justify-start">
                  <div className="bg-white border border-neutral-200 rounded-lg px-3 py-2 text-sm text-neutral-500">
                    Bot escribiendo...
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                value={testerInput}
                onChange={(e) => setTesterInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendTester()}
                placeholder="Escribe como si fueras un pasajero..."
                className="office-input flex-1 !text-sm"
                disabled={testerBusy}
                data-testid="tester-input"
              />
              <button
                onClick={sendTester}
                disabled={!testerInput.trim() || testerBusy}
                className="btn-primary"
                data-testid="tester-send-btn"
              >
                <Send className="w-4 h-4" strokeWidth={2.5} />
                Enviar
              </button>
              <button
                onClick={() => setTesterHistory([])}
                disabled={testerHistory.length === 0}
                className="btn-ghost !text-xs"
                data-testid="tester-clear-btn"
              >
                Limpiar
              </button>
            </div>
            {dirty && (
              <div className="mt-2 text-[11px] text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Guarda los cambios de arriba para probar con la nueva configuración.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="office-card grid grid-cols-1 md:grid-cols-3 min-h-[600px] overflow-hidden">
        <aside className="border-r border-neutral-200 overflow-y-auto max-h-[600px]">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Conversaciones ({conversations.length})
            </div>
            <button className="btn-ghost !p-1" onClick={loadConvs} data-testid="refresh-convs-btn">
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-neutral-500 text-sm">
              Sin conversaciones aún.
              <div className="text-xs mt-2 opacity-70">
                Escríbele al número{" "}
                <span className="font-mono">{status?.from_number || "…"}</span> desde otro
                celular para probar.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {conversations.map((c) => (
                <button
                  key={c.from_number}
                  onClick={() => setSelected(c)}
                  className={`w-full text-left px-4 py-3 hover:bg-neutral-50 ${
                    selected?.from_number === c.from_number ? "bg-[#EEF0FC]" : ""
                  }`}
                  data-testid={`bot-conv-${c.from_number}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-white text-[#3949AB] flex items-center justify-center font-semibold text-xs border">
                      {(c.contact_name || "??").split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{c.contact_name || c.from_number}</div>
                      <div className="text-xs text-neutral-500 font-mono truncate">{c.from_number}</div>
                    </div>
                    {c.unread_count > 0 && (
                      <span className="chip chip-success !text-[10px] !py-0.5">{c.unread_count}</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    {c.handoff && (
                      <span className="chip chip-warning !text-[10px] !py-0.5">Operador</span>
                    )}
                    <span className="text-[10px] text-neutral-500 truncate flex-1">
                      {(c.last_message || "").slice(0, 40)}
                    </span>
                    <span className="text-[10px] text-neutral-400">
                      {new Date(c.last_message_at).toLocaleTimeString("es-PE", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="md:col-span-2 flex flex-col max-h-[600px]">
          {selected ? (
            <>
              <div className="px-4 py-3 border-b flex items-center justify-between bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#EEF0FC] text-[#3949AB] flex items-center justify-center font-semibold">
                    {(selected.contact_name || "??").split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold">{selected.contact_name || "Sin nombre"}</div>
                    <div className="text-xs text-neutral-500 font-mono">{selected.from_number}</div>
                  </div>
                </div>
                {selected.handoff && (
                  <span className="chip chip-warning">Modo manual</span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#F8F9FC]" data-testid="messages-area">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "in" ? "justify-start" : "justify-end"}`}>
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-line ${
                        m.direction === "in" ? "bg-white border border-neutral-200" : "bg-[#3949AB] text-white"
                      }`}
                    >
                      {m.body}
                      <div
                        className={`text-[10px] mt-1 flex items-center gap-1 ${
                          m.direction === "in" ? "text-neutral-400" : "text-white/70"
                        }`}
                      >
                        {new Date(m.timestamp).toLocaleTimeString("es-PE", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {m.ai_action && (
                          <span
                            className={`chip chip-${ACTION_META[m.ai_action]?.color || "info"} !text-[9px] !py-0 !px-1 ml-1`}
                          >
                            {ACTION_META[m.ai_action]?.label || m.ai_action}
                          </span>
                        )}
                        {m.direction === "out" && <CheckCheck className="w-3 h-3 inline ml-1" />}
                      </div>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="text-center text-neutral-400 text-sm py-8">Sin mensajes.</div>
                )}
              </div>
              <form onSubmit={send} className="border-t px-3 py-2 flex items-center gap-2 bg-white">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Enviar mensaje manual (toma control humano)..."
                  className="office-input flex-1"
                  data-testid="bot-reply-input"
                />
                <button
                  type="submit"
                  disabled={sending || !reply.trim()}
                  className="btn-primary"
                  data-testid="bot-reply-send"
                >
                  <Send className="w-4 h-4" strokeWidth={2.5} />
                  {sending ? "..." : "Enviar"}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-neutral-400">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                Selecciona una conversación
                <div className="text-xs mt-2 max-w-xs opacity-70">
                  El bot IA responde automáticamente. Puedes tomar control cuando quieras.
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
